import { useMemo, useState } from 'react';
import { levels } from './types.js';
import styles from './HierarchicalGrid.module.css';

const gridColumns = [
  { key: 'configName', label: 'Config Name' },
  { key: 'assignedSite', label: 'Assigned Site' },
  { key: 'designedFor', label: 'Designed For' },
  { key: 'anomalyName', label: 'Anomaly Name' },
  { key: 'anomalyColor', label: 'Anomaly Color' },
  { key: 'severityLevel', label: 'Severity' },
  { key: 'comment', label: 'Comment' },
  { key: 'note', label: 'Note' },
  { key: 'action', label: 'Action' },
  { key: 'acceptanceLevel', label: 'Acceptance Level' }
];

const initialRows = [
  {
    id: "config-1",
    type: "config",
    values: {
      configName: "Config Name 1",
      assignedSite: "Site 1",
      designedFor: "Asset 1",
    },
    children: [
      {
        id: "anomaly-1",
        type: "anomaly",
        values: {
          anomalyName: "Custom Anomaly",
          anomalyColor: "#365da5",
        },
        children: [
          {
            id: "severity-1",
            type: "severity",
            values: {
              severityLevel: "1",
              comment: "Comment for this",
              note: "Additional note",
              action: "What to do",
              acceptanceLevel: "Acceptable to Criteria",
            },
            children: [],
          },
          {
            id: "severity-2",
            type: "severity",
            values: {
              severityLevel: "2",
              comment: "Comment for this",
              note: "Additional note",
              action: "What to do",
              acceptanceLevel: "Acceptable to Criteria",
            },
            children: [],
          },
          {
            id: "severity-3",
            type: "severity",
            values: {
              severityLevel: "3",
              comment: "Comment for this",
              note: "Additional note",
              action: "What to do",
              acceptanceLevel: "Acceptable to Criteria",
            },
            children: [],
          },
          {
            id: "severity-4",
            type: "severity",
            values: {
              severityLevel: "4",
              comment: "Comment for this",
              note: "Additional note",
              action: "What to do",
              acceptanceLevel: "Acceptable to Criteria",
            },
            children: [],
          },
        ],
      },
    ],
  },
];


function leafCount(node) {
  if (!node.children?.length) {
    return 1;
  }

  return node.children.reduce((total, child) => total + leafCount(child, 0));
}

function flattenTree(nodes, level = 0, path = []) {
  return nodes.flatMap((node) => {
    const currentPath = [...path, node];

    if (!node.children?.length) {
      return [
        {
          id: currentPath.map((item) => item.id).join("-"),
          path: currentPath,
        },
      ];
    }

    return flattenTree(node.children, level + 1, currentPath);
  });
}

export function HierarchicalGrid({
  columns,
  rows,
  onAdd,
}) {
  const [isEditing, setIsEditing] = useState(false);

  const rowsByLevel = useMemo(() => {
    return columns.reduce((result, column) => {
      result[column.level] ??= [];
      result[column.level].push(column);
      return result;
    }, {});
  }, [columns]);

  const levels = Object.keys(rowsByLevel).map(Number).sort((a, b) => a - b);
  const leafRows = useMemo(() => flattenTree(rows), [rows]);

  const renderedNodeIds = new Set();

  return (
    <div
      className={styles.wrapper}
    >
      <div
        className={styles.header}
        style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
      >
        {columns.map((column) => (
          <div key={column.key} className={styles.headerCell}>
            {column.label}
          </div>
        ))}
      </div>

      <div className={styles.body}>
        {leafRows.map((row) => (
          <div
            className={styles.row}
            style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
            key={row.id}
          >
            {levels.flatMap((level) => {
              const node = row.path[level];
              const levelColumns = rowsByLevel[level];

              if (!node) {
                return levelColumns.map((column) => (
                  <div key={`empty-${level}-${column.key}`} />
                ));
              }

              const nodeKey = `${level} - ${node.id}`;
              const isFirstOccurence = !renderedNodeIds.has(nodeKey);

              if (!isFirstOccurence) {
                return [];
              }

              renderedNodeIds.add(nodeKey);

              const parentPath = row.path.slice(0, level);

              return levelColumns.map((column) => (
                <div
                  key={`${node.id}-${column.key}`}
                  style={{ "--column-count": leafCount(node) }}
                >
                  <span>{node[column.key] ?? "-"}</span>

                  {isEditing && column.additive && (
                    <button
                      type="button"
                      onClick={() =>
                        onAdd({ level, column, parentPath })
                      }
                    >
                      + Add {column.addLabel ?? column.label}
                    </button>
                  )}
                </div>
              ))

            })}
          </div>
        ))}
      </div>
    </div >
  )
}


function AddRowForm({
  parentId,
  childType,
  onAdd
}) {
  const [values, setValues] = useState({});
  const config = levels[childType];

  function handleChange(event) {
    const { name, value } = event.target;

    setValues((currentValues) => ({
      ...currentValues,
      [name]: value
    }));
  };

  function handleSubmit(event) {
    event.preventDefault();

    onAdd(parentId, {
      id: crypto.randomUUID(),
      type: childType,
      values,
      children: [],
    });

    setValues({});
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${config.columns.length}, 1fr)`,
        gap: 8,
        alignItem: "center",
        flexWrap: "wrap",
      }}
    >
      {config.addFields.map((fieldKey) => {
        const column = config.columns.find(
          (currentColumn) => currentColumn.key === fieldKey
        );

        return (
          <input
            key={fieldKey}
            name={fieldKey}
            value={values[fieldKey] || ""}
            onChange={handleChange}
            placeholder={column?.label || fieldKey}
            required
            style={{ padding: 8 }}
          />
        );
      })}

      <button type="submit" style={{ padding: "8px 12px" }}>
        Add {childType}
      </button>
    </form>
  );
}

function addChild(rows, parentId, newRow) {
  if (parentId === null) {
    return [...rows, newRow];
  }

  return rows.map((row) => {
    if (row.id === parentId) {
      return {
        ...row,
        children: [...HierarchicalGrid(row.children || []), newRow],
      };
    }

    return {
      ...row,
      children: addChild(row.children || [], parentId, newRow),
    };
  });
}

function TreeRow({
  row,
  depth,
  isEditing,
  onAdd,
  onUpdate,
}) {
  const [expanded, setExpanded] = useState(true);

  const config = levels[row.type];
  const canAddChildren = Boolean(config.childType);
  const hasChildren = row.children && row.children.legnth > 0;
  const canExpand = hasChildren || canAddChildren;

  const editableKeys = config.columns
    .filter((column) => column.editable !== false)
    .map((column) => column.key);

  function isFieldUsedByThisLevel(fieldKey) {
    return config.columns.some((column) => column.key === fieldKey);
  }

  return (
    <>
      <div className={styles.row} style={{ gridTemplateColumns: `repeat(${gridColumns.length}, 1fr)` }}>
        {gridColumns.map((column, index) => {
          const isFirstColumn = index === 0;
          const isEditableField =
            isEditing &&
            editableKeys.includes(column.key) &&
            isFieldUsedByThisLevel(column.key);

          return (
            <div
              key={column.key}
              style={{
                paddingLeft: isFirstColumn ? 12 + depth * 28 : 12,
              }}
            >
              {isFirstColumn && canExpand && (
                <button
                  type="button"
                  onClick={() => setExpanded((current) => !current)}
                  aria-label={expanded ? "Collapse row" : "Expand row"}
                >
                  {expanded ? "-" : "+"}
                </button>
              )}

              {isEditableField ? (
                <input
                  value={row.values[column.key] ?? ""}
                  onChange={(event) => onUpdate(row.id, column.key, event.target.value)}
                  aria-label={column.label}
                />
              ) : (row.values[column.key] ?? "-")}
            </div>
          )
        })}
      </div>

      {expanded && isEditing && canAddChildren && (
        <div
          className={styles.row}
          style={{
            gridTemplateColumns: `repeat(${gridColumns.length}, 1fr)`
          }}
        >
          <div style={{ columnSpan: gridColumns.length }}>
            <AddRowForm
              parentId={row.id}
              childType={config.childType}
              onAdd={onAdd}
            />
          </div>
        </div>
      )}

      {expanded &&
        row.children?.map((child) => (
          <TreeRow
            key={child.id}
            row={child}
            depth={depth + 1}
            isEditing={isEditing}
            onAdd={onAdd}
            onUpdate={onUpdate}
          />
        ))}
    </>
  )
}

function RecursiveHierarchicalGrid({
  rows,
  rootType,
  isEditing,
  onAdd,
  onUpdate,
}) {
  return (
    <div className={styles.wrapper}>
      <div className={styles.header} style={{ gridTemplateColumns: `repeat(${gridColumns.length}, 1fr)` }}>
        {gridColumns.map((column) => (
          <div key={column.key}>
            {column.label}
          </div>
        ))}
      </div>

      {rows.map((row) => (
        <TreeRow
          key={row.id}
          row={row}
          depth={0}
          isEditing={isEditing}
          onAdd={onAdd}
          onUpdate={onUpdate}
        />
      ))}

      {isEditing && (
        <div className={styles.row} style={{ gridTemplateColumns: `repeat(${gridColumns.length}, 1fr)` }}>
          <div style={{ columnSpan: gridColumns.length }}>
            <AddRowForm
              parentId={null}
              childType={rootType}
              onAdd={onAdd}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function updateRowValue(rows, rowId, fieldKey, value) {
  return rows.map((row) => {
    if (row.id === rowId) {
      return {
        ...row,
        values: {
          ...row.values,
          [fieldKey]: value,
        },
      };
    }

    return {
      ...row,
      children: updateRowValue(row.children || [], rowId, fieldKey, value),
    };
  });
}

export function HierarchicalGridBuilder() {
  const [rows, setRows] = useState(initialRows);
  const [isEditing, setIsEditing] = useState(false);

  function handleAdd(parentId, newRow) {
    setRows((currentRows) =>
      addChild(currentRows, parentId, newRow));
  }

  function handleUpdate(rowId, fieldKey, value) {
    setRows((currentRows) =>
      updateRowValue(currentRows, rowId, fieldKey, value));
  }

  return (
    // <HierarchicalGrid
    //   columns={columns}
    //   rows={initialData}
    // />
    <div className={styles.container}>
      <button
        className={styles.editButton}
        type="button"
        onClick={() => setIsEditing((value) => !value)}>
        {isEditing ? "Done" : "Edit"
        }
      </button>

      <RecursiveHierarchicalGrid
        rows={rows}
        rootType="config"
        isEditing={isEditing}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
      />
    </div>
  )
}